import type { Folder, Instant, Note } from "@hq/contracts";
import {
  calendarDateOf,
  daysBetween,
  type FolderTreeNode,
  type NowContext,
  UNTITLED_NOTE,
} from "@hq/core";

/** `dataTransfer` type carrying a note id while a NoteList row is dragged. */
export const NOTE_DRAG_MIME = "application/x-hq-note";
/** `dataTransfer` type carrying a folder id while a FolderTree row is dragged. */
export const FOLDER_DRAG_MIME = "application/x-hq-folder";

/**
 * A `sortOrder` that sorts strictly between two neighbours. Either side may be absent
 * (dropping at the start or end of a list); with no neighbours at all the result is 0.
 */
export function sortOrderBetween(prev: number | undefined, next: number | undefined): number {
  if (prev === undefined && next === undefined) return 0;
  if (prev === undefined) return next! - 1;
  if (next === undefined) return prev + 1;
  return (prev + next) / 2;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/**
 * "just now", "5m ago", "3h ago", "Yesterday", "4d ago", then a short date. Calendar words
 * ("Yesterday") follow the zone in `ctx`, so a note edited at 23:50 reads "Yesterday" at 00:10.
 */
export function relativeTime(instant: Instant, ctx: NowContext): string {
  const then = Date.parse(instant);
  const now = Date.parse(ctx.now);
  const diff = now - then;
  if (diff < MINUTE) return "just now";
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`;
  const days = daysBetween(
    calendarDateOf(instant, ctx.timeZone),
    calendarDateOf(ctx.now, ctx.timeZone),
  );
  if (days <= 0) return `${Math.floor(diff / HOUR)}h ago`;
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  const sameYear = instant.slice(0, 4) === ctx.now.slice(0, 4);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: ctx.timeZone,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(new Date(then));
}

/** Long-form date and time for metadata lines: "Sep 16, 2026, 10:00". */
export function formatDateTime(instant: Instant, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(instant));
}

/** Strip inline markdown so a line reads as plain text. */
function plainText(line: string): string {
  return line
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(
      /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g,
      (_m, target: string, alias?: string) => alias ?? target,
    )
    .replace(/`([^`]*)`/g, "$1")
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/~~(.*?)~~/g, "$1")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * One line of preview text: the first non-empty line that is not a heading, with list
 * markers, block quotes, front matter and code fences removed. Empty when there is none.
 */
export function notePreview(markdown: string, maxLength = 140): string {
  const lines = markdown.split("\n");
  let i = 0;
  // YAML front matter.
  if (lines[0]?.trim() === "---") {
    const end = lines.findIndex((l, idx) => idx > 0 && l.trim() === "---");
    if (end > 0) i = end + 1;
  }
  let inFence = false;
  for (; i < lines.length; i++) {
    const raw = lines[i]!.trim();
    if (/^(```|~~~)/.test(raw)) {
      inFence = !inFence;
      continue;
    }
    if (inFence || !raw) continue;
    if (/^#{1,6}\s/.test(raw)) continue;
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(raw)) continue;
    const body = raw
      .replace(/^>\s?/, "")
      .replace(/^(\s*[-*+]\s+)?\[[ xX]\]\s+/, "")
      .replace(/^[-*+]\s+/, "")
      .replace(/^\d+[.)]\s+/, "")
      .replace(/^\|/, "");
    const text = plainText(body);
    if (!text) continue;
    return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
  }
  return "";
}

/** Display title, never blank. */
export function displayTitle(note: Pick<Note, "title">): string {
  return note.title.trim() || UNTITLED_NOTE;
}

/**
 * The folder a note appears under. A note whose folder is missing or tombstoned shows at
 * root level (`null`) so deleting a folder never hides its notes.
 */
export function effectiveFolderId(
  note: Pick<Note, "folderId">,
  folders: readonly Folder[],
): string | null {
  if (!note.folderId) return null;
  const folder = folders.find((f) => f.id === note.folderId);
  return folder && folder.deletedAt === undefined ? folder.id : null;
}

/** Live note counts keyed by effective folder id; `null` is the root. */
export function countNotesByFolder(
  notes: readonly Note[],
  folders: readonly Folder[],
): Map<string | null, number> {
  const out = new Map<string | null, number>();
  for (const note of notes) {
    const key = effectiveFolderId(note, folders);
    out.set(key, (out.get(key) ?? 0) + 1);
  }
  return out;
}

export type FlatFolderRow = {
  folder: Folder;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
  /** Siblings (including this folder) in display order, for reordering. */
  siblings: readonly Folder[];
};

/** Depth-first rows of a tree, descending only into expanded nodes. */
export function flattenFolderTree(
  tree: readonly FolderTreeNode[],
  isExpanded: (folderId: string, depth: number) => boolean,
): FlatFolderRow[] {
  const out: FlatFolderRow[] = [];
  const walk = (nodes: readonly FolderTreeNode[], depth: number) => {
    const siblings = nodes.map((n) => n.folder);
    for (const node of nodes) {
      const hasChildren = node.children.length > 0;
      const expanded = hasChildren && isExpanded(node.folder.id, depth);
      out.push({ folder: node.folder, depth, hasChildren, expanded, siblings });
      if (expanded) walk(node.children, depth + 1);
    }
  };
  walk(tree, 0);
  return out;
}

export type DropZone = "before" | "inside" | "after";

/**
 * Where a drop lands on a row from the pointer's vertical position as a fraction of the
 * row's height: the top quarter reorders before, the bottom quarter after, the middle nests.
 * Rows that cannot nest pass `canNest: false` and only ever nest.
 */
export function dropZoneFor(ratio: number, canNest = true): DropZone {
  if (!canNest) return "inside";
  if (ratio < 0.25) return "before";
  if (ratio > 0.75) return "after";
  return "inside";
}

/**
 * The `sortOrder` for `movingId` when dropped `zone` relative to `targetId` among `siblings`
 * (in display order). The moving folder is ignored so dropping next to yourself is a no-op.
 */
export function reorderSortOrder(
  siblings: readonly Folder[],
  movingId: string,
  targetId: string,
  zone: "before" | "after",
): number | undefined {
  const rest = siblings.filter((f) => f.id !== movingId);
  const index = rest.findIndex((f) => f.id === targetId);
  if (index < 0) return undefined;
  const prev = zone === "before" ? rest[index - 1] : rest[index];
  const next = zone === "before" ? rest[index] : rest[index + 1];
  return sortOrderBetween(prev?.sortOrder, next?.sortOrder);
}

// Unicode "Cc" is the control-character category (U+0000–U+001F, U+007F–U+009F).
const CONTROL_CHARS = /\p{Cc}/gu;

/** A file or directory name safe on every desktop filesystem. */
export function sanitizeFileName(name: string, fallback = UNTITLED_NOTE): string {
  const cleaned = name
    .replace(CONTROL_CHARS, "")
    .replace(/[/\\:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/^\.+/, "")
    .trim()
    .replace(/[. ]+$/, "");
  const limited = cleaned.length > 120 ? cleaned.slice(0, 120).trim() : cleaned;
  return limited || fallback;
}

/** `base`, else `base (2)`, `base (3)`, … until unused in `taken`; records the result. */
export function uniqueName(taken: Set<string>, base: string, extension = ""): string {
  let candidate = `${base}${extension}`;
  let n = 2;
  while (taken.has(candidate.toLowerCase())) {
    candidate = `${base} (${n})${extension}`;
    n += 1;
  }
  taken.add(candidate.toLowerCase());
  return candidate;
}

export const JOURNAL_FOLDER_NAME = "Journal";

/** The live folder named "Journal" (case-insensitive), where daily notes go by default. */
export function journalFolderId(folders: readonly Folder[]): string | undefined {
  const lower = JOURNAL_FOLDER_NAME.toLowerCase();
  return folders.find((f) => f.deletedAt === undefined && f.name.trim().toLowerCase() === lower)
    ?.id;
}
